import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import './NotificationBell.css';

// The 'news' key is the internal type stored in saved inboxes; it is shown to users as "Update".
const TYPE_META = {
  news: { label: 'Update', tone: 'news' },
  event: { label: 'Event', tone: 'event' },
  vacancy: { label: 'Vacancy', tone: 'vacancy' },
  general: { label: 'Update', tone: 'general' },
};

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'happening', label: "What's Happening" },
  { id: 'vacancy', label: 'Vacancies' },
];

function timeAgo(timestamp) {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;

  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;

  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;

  return new Date(timestamp).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
  });
}

function BellIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

function SoundIcon({ muted }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M11 5 6.5 9H3v6h3.5L11 19V5Z" />
      {muted ? (
        <path d="m16 9 5 6M21 9l-5 6" />
      ) : (
        <>
          <path d="M15.5 8.5a4.5 4.5 0 0 1 0 7" />
          <path d="M18.5 6a8 8 0 0 1 0 12" />
        </>
      )}
    </svg>
  );
}

function ItemIcon({ type }) {
  if (type === 'vacancy') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M9 7V5.5A2.5 2.5 0 0 1 11.5 3h1A2.5 2.5 0 0 1 15 5.5V7" />
        <rect x="3" y="7" width="18" height="13" rx="2.5" />
        <path d="M3 12h18" />
      </svg>
    );
  }

  if (type === 'event') {
    return (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="5" width="18" height="16" rx="2.5" />
        <path d="M3 10h18M8 3v4M16 3v4" />
        <path d="M8.5 14.5h3v3h-3z" />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M5 6.5h11a2 2 0 0 1 2 2V17a1.5 1.5 0 0 0 3 0V8.5" />
      <path d="M5 6.5a2 2 0 0 0-2 2V17a3 3 0 0 0 3 3h12" />
      <path d="M8 10.5h7M8 13.5h7" />
    </svg>
  );
}

export default function NotificationBell() {
  const {
    inbox,
    unreadCount,
    muted,
    toggleMuted,
    markAllRead,
    markOneRead,
    clearInbox,
    removeOne,
  } = useNotifications();

  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState('all');
  const [pulse, setPulse] = useState(false);

  const wrapperRef = useRef(null);
  const navigate = useNavigate();
  const prevUnreadRef = useRef(unreadCount);

  const sortedInbox = useMemo(
    () => [...inbox].sort((a, b) => b.timestamp - a.timestamp),
    [inbox]
  );

  const visibleInbox = useMemo(() => {
    if (filter === 'all') return sortedInbox;
    if (filter === 'vacancy') {
      return sortedInbox.filter((item) => item.type === 'vacancy');
    }
    // "What's Happening" = updates + events
    return sortedInbox.filter(
      (item) => item.type === 'news' || item.type === 'event'
    );
  }, [sortedInbox, filter]);

  /* Animate the bell when a new notification lands */
  useEffect(() => {
    if (unreadCount > prevUnreadRef.current) {
      setPulse(true);
      const timer = window.setTimeout(() => setPulse(false), 900);
      prevUnreadRef.current = unreadCount;
      return () => window.clearTimeout(timer);
    }

    prevUnreadRef.current = unreadCount;
    return undefined;
  }, [unreadCount]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const handleToggle = () => {
    const nextOpen = !open;
    setOpen(nextOpen);
    if (nextOpen && unreadCount > 0) markAllRead();
  };

  const handleItemClick = (item) => {
    markOneRead(item.id);
    setOpen(false);
    if (item.link) navigate(item.link);
  };

  return (
    <div className="pgbell" ref={wrapperRef}>
      <button
        type="button"
        className={`pgbell__trigger ${pulse ? 'is-pulsing' : ''} ${
          open ? 'is-open' : ''
        }`}
        aria-label={`Notifications${unreadCount ? `, ${unreadCount} unread` : ''}`}
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={handleToggle}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="pgbell__badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="pgbell__panel" role="menu" aria-label="Notifications">
          <div className="pgbell__header">
            <div className="pgbell__heading">
              <span className="pgbell__eyebrow">Project Green</span>
              <h3 className="pgbell__title">Notifications</h3>
            </div>

            <div className="pgbell__headerActions">
              <span className="pgbell__counter">
                {unreadCount > 0
                  ? `${unreadCount} new`
                  : `${sortedInbox.length} total`}
              </span>

              <button
                type="button"
                className="pgbell__iconBtn"
                aria-label={muted ? 'Unmute notification sound' : 'Mute notification sound'}
                title={muted ? 'Sound off' : 'Sound on'}
                onClick={toggleMuted}
              >
                <SoundIcon muted={muted} />
              </button>
            </div>
          </div>

          <div className="pgbell__filters" role="tablist">
            {FILTERS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                role="tab"
                aria-selected={filter === tab.id}
                className={`pgbell__filter ${
                  filter === tab.id ? 'is-active' : ''
                }`}
                onClick={() => setFilter(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {visibleInbox.length === 0 ? (
            <div className="pgbell__empty">
              <span className="pgbell__emptyMark">
                <BellIcon />
              </span>
              <p className="pgbell__emptyTitle">You&rsquo;re all caught up</p>
              <p className="pgbell__emptyCopy">
                New vacancies and What&rsquo;s Happening updates will appear
                here and stay until you dismiss them.
              </p>
            </div>
          ) : (
            <ul className="pgbell__list">
              {visibleInbox.map((item) => {
                const meta = TYPE_META[item.type] || TYPE_META.general;

                return (
                  <li
                    key={item.id}
                    className={`pgbell__item pgbell__item--${meta.tone} ${
                      item.read ? '' : 'is-unread'
                    }`}
                  >
                    <button
                      type="button"
                      className="pgbell__itemMain"
                      onClick={() => handleItemClick(item)}
                    >
                      <span className="pgbell__itemIcon">
                        <ItemIcon type={item.type} />
                      </span>

                      <span className="pgbell__itemContent">
                        <span className="pgbell__itemTop">
                          <span className="pgbell__kicker">{meta.label}</span>
                          <span className="pgbell__time">
                            {timeAgo(item.timestamp)}
                          </span>
                        </span>

                        <span className="pgbell__itemTitle">{item.title}</span>

                        {item.body ? (
                          <span className="pgbell__itemBody">{item.body}</span>
                        ) : null}
                      </span>

                      {!item.read && <span className="pgbell__unreadDot" />}
                    </button>

                    <button
                      type="button"
                      className="pgbell__remove"
                      aria-label={`Dismiss ${item.title}`}
                      title="Dismiss"
                      onClick={() => removeOne(item.id)}
                    >
                      <svg
                        viewBox="0 0 24 24"
                        fill="none"
                        aria-hidden="true"
                        stroke="currentColor"
                        strokeWidth="1.9"
                        strokeLinecap="round"
                      >
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          {sortedInbox.length > 0 && (
            <div className="pgbell__footer">
              <button
                type="button"
                className="pgbell__footerBtn"
                onClick={markAllRead}
              >
                Mark all as read
              </button>
              <button
                type="button"
                className="pgbell__footerBtn pgbell__footerBtn--muted"
                onClick={clearInbox}
              >
                Dismiss all
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}